/**
 * WordPopup — tap an individual Arabic word span → word meaning + root in a SlideSheet.
 *
 * Looks up the tapped word text in the static word-roots dictionary.
 * If not found, shows a friendly "no entry" message instead of an empty state.
 *
 * Usage:
 *   import { showWordPopup } from './WordPopup';
 *   await showWordPopup(wordText, verseKey, ctx);
 */

import type { AweniContext } from '../../types';
import { SlideSheet } from './components/SlideSheet';
import { lookupWord } from './word-roots';

/**
 * Show a word-detail SlideSheet for the given Arabic word.
 * Resolves immediately after the sheet is open.
 */
export async function showWordPopup(
  wordText: string,
  verseKey: string,
  ctx: AweniContext,
): Promise<void> {
  const t = (key: string, fallback: string) => ctx.i18n.t(key) || fallback;

  // Haptic feedback
  navigator.vibrate?.(10);

  const entry = lookupWord(wordText);

  const sheet = new SlideSheet({
    title: `${wordText}  ·  ${verseKey}`,
    closeOnOverlay: true,
  });

  sheet.setContent(_renderContent(wordText, verseKey, entry, t));
  sheet.open();
}

// ─── HTML builder ──────────────────────────────────────────────────────────

function _renderContent(
  word: string,
  verseKey: string,
  entry: ReturnType<typeof lookupWord>,
  t: (k: string, fb: string) => string,
): string {
  let body: string;

  if (entry) {
    const rootDisplay = entry.root
      ? `<div class="hwp-row">
           <span class="hwp-label">${escHtml(t('hisni_word_root', 'Root'))}</span>
           <span class="hwp-value hwp-root" dir="rtl" lang="ar">${escHtml(entry.root)}</span>
         </div>`
      : '';

    const notesDisplay = entry.notes
      ? `<div class="hwp-row">
           <span class="hwp-label">${escHtml(t('hisni_word_notes', 'Grammar'))}</span>
           <span class="hwp-value">${escHtml(entry.notes)}</span>
         </div>`
      : '';

    body = `
      <div class="hwp-word-display" dir="rtl" lang="ar" aria-label="${escHtml(t('hisni_word_label', 'Arabic word'))}">
        ${escHtml(word)}
      </div>
      <div class="hwp-detail-grid">
        <div class="hwp-row">
          <span class="hwp-label">${escHtml(t('hisni_word_transliteration', 'Transliteration'))}</span>
          <span class="hwp-value hwp-translit">${escHtml(entry.transliteration)}</span>
        </div>
        <div class="hwp-row hwp-row--meaning">
          <span class="hwp-label">${escHtml(t('hisni_word_meaning', 'Meaning'))}</span>
          <span class="hwp-value hwp-meaning">${escHtml(entry.meaning)}</span>
        </div>
        ${rootDisplay}
        ${notesDisplay}
      </div>
    `;
  } else {
    body = `
      <div class="hwp-word-display" dir="rtl" lang="ar" aria-label="${escHtml(t('hisni_word_label', 'Arabic word'))}">
        ${escHtml(word)}
      </div>
      <p class="hwp-no-entry">
        ${escHtml(t('hisni_word_not_found', 'No dictionary entry found for this word.'))}
      </p>
    `;
  }

  return `${body}${_styleBlock()}`;
}

let _stylesInjected = false;
function _styleBlock(): string {
  if (_stylesInjected) return '';
  _stylesInjected = true;
  return `<style id="hwp-styles">
    .hwp-word-display {
      font-family: var(--ln-font-arabic, 'Amiri', serif);
      font-size: clamp(1.6rem, 6vw, 2.4rem);
      font-weight: 700;
      color: var(--ln-text-primary, #111);
      text-align: center;
      padding: 16px 8px 20px;
      line-height: 1.4;
      letter-spacing: .04em;
    }

    .hwp-detail-grid {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .hwp-row {
      display: flex;
      align-items: baseline;
      gap: 10px;
      padding: 8px 10px;
      background: var(--ln-bg-subtle, rgba(0,0,0,.03));
      border-radius: var(--ln-radius-sm, 6px);
    }
    .hwp-row--meaning {
      background: var(--ln-bg-card, #fff);
      border: 1px solid var(--ln-border, rgba(0,0,0,.08));
    }

    .hwp-label {
      font-size: .72rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: .06em;
      color: var(--ln-text-secondary);
      flex-shrink: 0;
      min-width: 90px;
    }

    .hwp-value {
      font-size: .92rem;
      color: var(--ln-text-primary, #111);
      line-height: 1.5;
    }
    .hwp-translit {
      font-style: italic;
      color: var(--ln-accent, #0A0A0A);
    }
    .hwp-meaning {
      font-weight: 600;
    }
    .hwp-root {
      font-family: var(--ln-font-arabic, 'Amiri', serif);
      font-size: 1rem;
      letter-spacing: .1em;
    }

    .hwp-no-entry {
      font-size: .9rem;
      color: var(--ln-text-secondary);
      text-align: center;
      padding: 24px 16px;
      margin: 0;
    }

    .dark .hwp-row--meaning {
      background: rgba(255,255,255,.05);
      border-color: rgba(255,255,255,.10);
    }
    .dark .hwp-row {
      background: rgba(255,255,255,.04);
    }
  </style>`;
}

function escHtml(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
